<template>
  <SettingsCard
    :title="$t('settings.features.contact.title')"
    :subtitle="$t('settings.features.contact.subtitle')"
    icon="mdi-cog"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <div class="pt-4 px-4">
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div>
            <div class="d-flex align-center">
              <span class="font-weight-bold">
                {{ $t('settings.features.contact.body-texts.0') }}
              </span>
            </div>
            <span class="text-grey">
              {{ $t('settings.features.contact.body-texts.1') }}
            </span>
          </div>
        </v-col>
        <v-col
          cols="12"
          md="6"
          class="px-0 px-md-2"
        >
          <TextForm
            :fields="contactFields"
            @update="updateContact"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="px-0 px-md-2"
        >
          <div>
            <div class="d-flex align-center">
              <span class="font-weight-bold">
                {{ $t('settings.features.contact.body-texts.2') }}
              </span>
            </div>
            <span class="text-grey">
              {{ $t('settings.features.contact.body-texts.3') }}
            </span>
          </div>
        </v-col>

        <v-col
          cols="12"
          md="6"
          class="px-0 px-md-2"
        >
          <PhoneField
            :model-value="phone"
            update-on-input
            @update:model-value="(val) => updateContact({ phone: val })"
          />
          <PhoneField
            :model-value="mobile"
            :label="$t('settings.features.contact.labels.mobile')"
            update-on-input
            @update:model-value="(val) => updateContact({ mobile: val })"
          />
          <div class="py-6">
            <TextForm
              :fields="emailField"
              @update="updateContact"
            />
          </div>
        </v-col>
      </v-row>
      <Feature :feature-slug="featureNames.GOOGLE_REVIEW">
        <v-row>
          <v-col
            cols="12"
            md="6"
            class="px-0 px-md-2"
          >
            <div class="font-weight-bold">
              {{ $t('settings.features.contact.google-review.title') }}
            </div>
            <div class="text-grey mb-4">
              {{ $t('settings.features.contact.google-review.description') }}
            </div>
            <a
              class="google-review-link"
              :href="$t('settings.features.contact.google-review.link')"
              target="_blank"
              rel="noopener"
            >
              {{ $t('settings.features.contact.google-review.link') }}
            </a>
          </v-col>
          <v-col
            cols="12"
            md="6"
            class="px-0 px-md-2"
          >
            <v-tooltip
              location="top"
              :disabled="!!googleReviewLink"
            >
              <span>{{ $t('tooltips.google-review-not-set') }}</span>
              <template #activator="{ props }">
                <div
                  v-bind="props"
                  style="width: fit-content"
                >
                  <v-switch
                    v-model="appendGoogleReviewLink"
                    :loading="updateIncludeGoogleReviewLoading"
                    color="primary"
                  />
                </div>
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
      </Feature>
    </div>
  </SettingsCard>
</template>

<script>
import PhoneField from '@/components/PhoneField.vue'
import SettingsCard from '@/components/SettingsCard.vue'
import TextForm from '@/components/TextForm.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import bus, { eventNames } from '@/lib/eventBus'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import featureMixin from '@/mixins/feature'
import UPDATE_AMBASSADOR from '@/modules/settings/companySettings/queries/UpdateAmbassador.gql'
import UPDATE_COMPANY_AMBASSADOR_SIGNATURE_INCLUDE_GOOGLE_REVIEW from '@/modules/settings/emailSettings/queries/UpdateCompanyAmbassadorSignatureIncludeGoogleReview.gql'

export default {
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'public-contact' })
    return {
      isOpen,
      setOpen,
    }
  },
  components: {
    SettingsCard,
    TextForm,
    PhoneField,
  },
  mixins: [featureMixin],
  props: {
    company: {
      type: Object,
      default: () => ({}),
    },
    refetchCompany: {
      type: Function,
      default: () => {},
    },
    googleReviewLink: {
      type: String,
      default: null,
    },
    signature: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      includeGoogleReview: false,
      phone: this.company.ambassador.phone,
      mobile: this.company.ambassador.mobile,
      updateIncludeGoogleReviewLoading: false,
    }
  },
  computed: {
    appendGoogleReviewLink: {
      get() {
        return this.includeGoogleReview
      },
      set(value) {
        this.handleIncludeGoogleReviewChange(value)
      },
    },
    contactFields() {
      return [
        {
          label: this.$t('settings.features.contact.labels.name'),
          name: 'name',
          placeholder: '',
          required: false,
          value: this.company.ambassador.name,
        },
        {
          label: this.$t('settings.features.contact.labels.role'),
          name: 'role',
          placeholder: '',
          required: false,
          value: this.company.ambassador.role,
        },
      ]
    },
    emailField() {
      return [
        {
          label: this.$t('settings.features.contact.labels.email'),
          name: 'email',
          placeholder: '',
          required: false,
          value: this.company.ambassador.email,
        },
      ]
    },
  },
  methods: {
    async handleIncludeGoogleReviewChange(value) {
      this.includeGoogleReview = value
      this.updateIncludeGoogleReviewLoading = true
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY_AMBASSADOR_SIGNATURE_INCLUDE_GOOGLE_REVIEW,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              includeGoogleReview: value,
            },
          },
        })
        this.$tracking.event(
          'Settings',
          'Clicked',
          'Update Company Ambassador signature include Google Review'
        )
        showSnackbarMessage('success', this.$t('alerts.settings.email.signature-settings.success'))
        this.refetchCompany()
      } catch (err) {
        showSnackbarMessage('error', this.$t('alerts.settings.email.signature-settings.error'))
      } finally {
        this.updateIncludeGoogleReviewLoading = false
      }
    },
    async updateContact(values) {
      const fieldName = Object.keys(values)[0]

      try {
        await this.$apollo.mutate({
          mutation: UPDATE_AMBASSADOR,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              ...values,
            },
          },
        })
        this.refetchCompany()
        this.$tracking.event('Settings', 'Edited', fieldName, values[fieldName])
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.features.success'),
        })
      } catch (e) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.features.error'),
        })
      }
    },
  },
  watch: {
    'company.ambassador.signature': {
      handler(value) {
        this.includeGoogleReview = value.includeGoogleReview
      },
      immediate: true,
    },
  },
}
</script>
<style scoped>
.google-review-link {
  color: rgb(var(--v-theme-primary));
}
</style>
